import React, { useMemo, useState } from 'react';
import { useCart } from '../context/CartContext';

const Statistics = () => {
  const { orders } = useCart();
  const [selectedUser, setSelectedUser] = useState('all');
  const [selectedDate, setSelectedDate] = useState('');

  const allUsers = useMemo(() => {
    const userNames = new Set();
    orders.forEach(order => {
      const name = order.user?.name || order.userId?.firstName || order.userId?.username;
      if (name) userNames.add(name);
    });
    return ['all', ...Array.from(userNames)];
  }, [orders]);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const name = order.user?.name || order.userId?.firstName || order.userId?.username || '';
      const userMatch = selectedUser === 'all' || name === selectedUser;

      const rawDate = order.date || order.createdAt;
      let dateStr = '';
      try {
        const d = new Date(rawDate);
        if (!isNaN(d)) {
          dateStr = d.toISOString();
        }
      } catch (_) {
        return false;
      }

      const dateMatch = selectedDate ? dateStr.startsWith(selectedDate) : true;
      return userMatch && dateMatch;
    });
  }, [orders, selectedUser, selectedDate]);

  const generalStats = useMemo(() => {
    const groupByHour = {};
    const dishCount = {};
    let totalSpent = 0;

    orders.forEach(order => {
      try {
        const raw = order.date || order.createdAt;
        const d = new Date(raw);
        if (isNaN(d)) return;

        totalSpent += order.total || 0;
        const hour = d.getHours();
        groupByHour[hour] = (groupByHour[hour] || 0) + 1;

        (order.items || []).forEach(item => {
          if (item?.name && item?.quantity) {
            dishCount[item.name] = (dishCount[item.name] || 0) + item.quantity;
          }
        });
      } catch (_) {}
    });

    const topDishes = Object.entries(dishCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    const averagePerUser = orders.length > 0 ? (totalSpent / orders.length).toFixed(0) : 0;

    return {
      totalSpent,
      averagePerUser,
      topDishes,
      groupByHour
    };
  }, [orders]);

  const dailyStats = useMemo(() => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const weekAgo = new Date(now);
    const monthAgo = new Date(now);
    weekAgo.setDate(now.getDate() - 7);
    monthAgo.setMonth(now.getMonth() - 1);

    let totalMonthSpent = 0;

    const ordersToday = filteredOrders.filter(o => {
      try {
        const d = new Date(o.date || o.createdAt);
        return !isNaN(d) && d.toISOString().startsWith(today);
      } catch (_) {
        return false;
      }
    });

    const ordersThisWeek = filteredOrders.filter(o => {
      try {
        const d = new Date(o.date || o.createdAt);
        return !isNaN(d) && d > weekAgo;
      } catch (_) {
        return false;
      }
    });

    const ordersThisMonth = filteredOrders.filter(o => {
      try {
        const d = new Date(o.date || o.createdAt);
        if (!isNaN(d) && d > monthAgo) {
          totalMonthSpent += o.total || 0;
          return true;
        }
      } catch (_) {}
      return false;
    });

    return {
      countToday: ordersToday.length,
      countWeek: ordersThisWeek.length,
      countMonth: ordersThisMonth.length,
      totalMonthSpent
    };
  }, [filteredOrders]);

  return (
    <div className="text-white p-6 max-w-6xl mx-auto space-y-12">
      <h1 className="text-3xl font-bold mb-4">📊 Статистика заказов</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded p-4 shadow space-y-4">
          <label className="block text-sm font-medium text-gray-300">Фильтр по пользователю:</label>
          <select
            className="bg-gray-900 border border-gray-700 text-white rounded p-2 text-sm w-full"
            value={selectedUser}
            onChange={e => setSelectedUser(e.target.value)}
          >
            {allUsers.map(user => (
              <option key={user} value={user}>{user === 'all' ? 'Все пользователи' : user}</option>
            ))}
          </select>
        </div>

        <div className="bg-gray-800 rounded p-4 shadow space-y-4">
          <label className="block text-sm font-medium text-gray-300">Фильтр по дате:</label>
          <input
  type="date"
  className="bg-gray-900 border border-gray-700 text-white rounded p-2 text-sm w-full pr-10 appearance-none"
  value={selectedDate}
  onChange={e => setSelectedDate(e.target.value)}
  style={{
    backgroundImage: 'url("data:image/svg+xml,%3Csvg fill=\'white\' xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 24 24\'%3E%3Cpath d=\'M19 4h-1V2h-2v2H8V2H6v2H5c-1.1 0-1.99.9-1.99 2L3 20c0 1.1.89 2 1.99 2H19c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zM5 20V9h14v11H5z\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.75rem center',
    backgroundSize: '1rem'
  }}
/>

        </div>
      </div>

      <div className="bg-gray-800 rounded p-6 shadow space-y-4">
        <h2 className="text-xl font-semibold">📦 Количество заказов</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          <div className="bg-gray-900 rounded p-4">
            <h3 className="text-lg font-semibold">📆 Сегодня</h3>
            <p className="text-2xl mt-2">{dailyStats.countToday}</p>
          </div>
          <div className="bg-gray-900 rounded p-4">
            <h3 className="text-lg font-semibold">🗓️ За неделю</h3>
            <p className="text-2xl mt-2">{dailyStats.countWeek}</p>
          </div>
          <div className="bg-gray-900 rounded p-4">
            <h3 className="text-lg font-semibold">📅 За месяц</h3>
            <p className="text-2xl mt-2">{dailyStats.countMonth}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div className="bg-gray-800 rounded p-6 shadow">
          <h2 className="text-xl font-semibold">💰 Потрачено за всё время</h2>
          <p className="text-3xl mt-3">{generalStats.totalSpent.toLocaleString()} ₽</p>
        </div>
        <div className="bg-gray-800 rounded p-6 shadow">
          <h2 className="text-xl font-semibold">📅 Потрачено за месяц</h2>
          <p className="text-3xl mt-3">{dailyStats.totalMonthSpent.toLocaleString()} ₽</p>
        </div>
        <div className="bg-gray-800 rounded p-6 shadow">
          <h2 className="text-xl font-semibold">👤 Средний чек</h2>
          <p className="text-3xl mt-3">{generalStats.averagePerUser} ₽</p>
        </div>
      </div>

      <div className="bg-gray-800 rounded p-6 shadow">
        <h2 className="text-xl font-semibold mb-4">🍱 Топ блюд по популярности</h2>
        <ul className="list-disc list-inside text-sm text-gray-300 space-y-1">
          {generalStats.topDishes.map(([name, count]) => (
            <li key={name}>{name} × {count}</li>
          ))}
        </ul>
      </div>

      <div className="bg-gray-800 rounded p-6 shadow">
        <h2 className="text-xl font-semibold mb-4">🕒 Заказы по часам</h2>
        <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-12 gap-2 text-sm">
          {Array.from({ length: 24 }, (_, hour) => (
            <div key={hour} className="flex flex-col items-center">
              <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded">
                {generalStats.groupByHour[hour] || 0}
              </div>
              <span className="text-gray-400 text-xs">{hour}:00</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Statistics;
